import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";

import { useLocalizedHref } from "@/libraries/web-framework";

import { ArrowRight, TestimonialCard } from "./components";

const sectionContainer = css({
    position: "relative",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    // gets rid of image border
    marginBlockEnd: "-1px",

    [media.above.md]: {
        padding: "100px 0"
    },
    [media.below.md]: {
        padding: "50px 0"
    }
});

const testimonialContainer = css({
    position: "relative",
    maxWidth: "100%",
    display: "flex",
    alignItems: "flex-start",
    [media.above.lg]: {
        gap: "150px"
    },
    [media.below.lg]: {
        flexDirection: "column",
        alignItems: "center",
        gap: "0px"
    }
});

const testimonialContentContainer = css({
    a: {
        color: "#0065FF",
        marginInlineEnd: "5px"
    },
    [media.only.md]: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
    },
    [media.only.sm]: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
    },
    [media.below.sm]: {
        maxWidth: "396px",
        padding: "0 25px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
    }
});

const leftContainer = css({
    display: "flex",
    alignItems: "flex-start",
    gap: "10px"
});

const contentStyles = css({
    [media.above.lg]: {
        maxWidth: "250px"
    },
    [media.only.md]: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: "25px",
        marginBlockEnd: "56px",
        width: "100%"
    },
    [media.only.sm]: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: "25px",
        marginBlockEnd: "56px",
        width: "100%"
    },
    [media.below.sm]: {
        maxWidth: "316px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: "25px",
        marginBlockEnd: "56px"
    }
});

const headingStyles = css({
    color: "var(--Neutrals-N0, #FFF)",
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: "var(--bxp-charlie-display-font-family)",
    fontStyle: "normal",
    fontWeight: "700",
    [media.above.lg]: {
        fontSize: "40px",
        lineHeight: "48px"
    },
    [media.only.md]: {
        fontSize: "48px",
        lineHeight: "normal"
    },
    [media.only.sm]: {
        fontSize: "40px",
        lineHeight: "normal"
    },
    [media.below.sm]: {
        fontSize: "30px",
        lineHeight: "normal"
    }
});

const subtitleStyles = css({
    marginBlockStart: "4px",
    color: "var(--Neutrals-N60, #B3BAC5)",
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: "var(--bxp-charlie-text-font-family)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "24px"
});

const linkStyles = css({
    marginBlockStart: "32px",
    [media.below.lg]: {
        marginBlockStart: 0
    }
});

const starStyles = css({
    width: "34px",
    height: "38px",
    [media.below.lg]: {
        display: "none"
    }
});

const shapeStyles = css({
    background: "#1868DB",
    position: "absolute",
    zIndex: "-2",
    transform: "rotate(180deg) scaleX(-1)",
    height: "478px",
    width: "100%",
    insetBlockEnd: "0px",
    [media.below.lg]: {
        display: "none"
    }
});

const mobileShapeStyles = css({
    background: "#1868DB",
    position: "absolute",
    zIndex: "-2",
    height: "1104px",
    width: "100%",
    insetBlockEnd: "0px",
    [media.above.lg]: {
        display: "none"
    }
});

export const TestimonialSection = () => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleClick = () => {
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            attributes: {
                name: "atlas-55799-testimonial-button-clicked",
                section: "testimonial-section"
            }
        }).fire();
    };

    return (
        <div css={sectionContainer}>
            <img
                src="https://wac-cdn.atlassian.com/misc-assets/webp-images/dark-shape.webp"
                alt="testimonial"
                css={shapeStyles}
            />
            <img
                src="https://wac-cdn.atlassian.com/misc-assets/webp-images/mobile-dark-shape.webp"
                alt="testimonial"
                css={mobileShapeStyles}
            />
            <div css={testimonialContainer}>
                <div css={leftContainer}>
                    <img
                        css={starStyles}
                        src="https://wac-cdn.atlassian.com/misc-assets/webp-images/star.webp"
                        alt="star"
                    />
                    <div css={testimonialContentContainer}>
                        <div css={contentStyles}>
                            <div css={headingStyles}>For teams big & small</div>
                            <div css={subtitleStyles}>
                                Hear from start-ups & large enterprises that
                                prefer Atlassian
                            </div>

                            <div css={linkStyles}>
                                <a
                                    href={useLocalizedHref("/customers/roblox")}
                                    onClick={handleClick}
                                >
                                    {"See more customer stories "}
                                    <ArrowRight />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <TestimonialCard
                    image="https://wac-cdn.atlassian.com/misc-assets/webp-images/testimonial-roblox.webp"
                    quote="Before, our team saw Atlassian as individual tools...Now, [features & integrations] like Jira macros & Smart Links have really been pivotal in collaboration, productivity, & discoverability."
                    name="Joe Cotant"
                    title="Senior Technical Program Manager, Roblox"
                />
            </div>
        </div>
    );
};
TestimonialSection.displayName = "TestimonialSection";
